import AccessControl from 'accesscontrol'
import keyMirror from 'keymirror'
import lodash from 'lodash'
import store from '@/store'
import { FEATURE_NAMES } from '@/utils/consts'

/**
 * @enum
 */
const Actions = keyMirror({
  createOwn: null,
  createAny: null,
  readOwn: null,
  readAny: null,
  updateOwn: null,
  updateAny: null,
  deleteOwn: null,
  deleteAny: null,
})
export const allStarAttr = '*'

/**
 * @enum
 */
export const SettingsReadAnyAttrs = {
  basic: 'basic',
  vendors: 'vendors',
  regional: 'regional',
}

export const SettingsUpdateAnyAttrs = {
  'basic': 'basic',
  regional: 'regional',
}

/**
 * @enum
 */
export const HotbAttrs = keyMirror({
  status: null,
  date: null,
  arrange: null,
  others: null,
  changePIC: null,
  resetToDraft: null,
})

/**
 * @enum
 */
export const TenantProjectAttrs = keyMirror({
  commentDesign: null,
  commentTech: null,
  greenFitout: null,
  preview: null,
  send: null,
  startGreenFitout: null,
  onHoldGreenFitout: null,
  reviewGreenFitout: null,
  commentOnGreenFitout: null,
  readGreenPoint: null,
  previewGreenFitout: null,
  sendGreenFitout: null,
  info: null,
  deposit: null,
  accounting: null,
  inspection: null,
  insurance: null,
  sendFinalDocsReminder: null,
  addReviewFinalDocFolder: null,
  completeFinalDocs: null,
  exemptFinalDocs: null,
  resumeDeposit: null,
})

/**
 * @enum
 */
export const NonTenantProjectAttr = keyMirror({
  approve: null,
  resetToDraft: null,
  changePIC: null,
  extendClosingDate: null,
})

export const tenderReadOwnAttrs = keyMirror({
  exportViewAll: null,
})

export const projectReadOwnAttrs = keyMirror({
  exportViewAll: null,
})

/**
 * @enum
 */
export const TenancyAttrs = keyMirror({
  detail: null,
  requestForInvitation: null,
  manageMarketingContacts: null,
  startOperation: null,
  sendOperationReminder: null,
  submitOperationDoc: null,
  reviewOperation: null,
  commentOperation: null,
  previewOperation: null,
  sendOperation: null,
  readOperationPoints: null,
  inviteMaster: null,
  contacts: null,
  leases: null,
  changeCompanyName: null,
  updateTenancyMasterAccount: null,
  updateRemarksAndDocs: null,
  changeTenancyStatus: null,
  updateTenancyRep: null,
})

/**
 * @enum
 */
export const WorkPermitAttrs = keyMirror({
  reviewPP: null,
  secondReviewPP: null,
  approvePP: null,
  reviewWP: null,
  secondReviewWP: null,
  thirdReviewWP: null,
  approveWP: null,
  approveOfficeHP: null,
  reviewNonOfficeHP: null,
  approveNonOfficeHP: null,
  followUpHoisting: null,
  updateWorkers: null,
  generateNotice: null,
  reassignReviewer: null,
})

/**
 * @enum
 */
export const VendorECircularAttrs = keyMirror({
  manageECircularContact: null,
})

/**
 * @enum
 */
export const DirectoryAttrs = keyMirror({
  maintainOrder: null,
  createUpdate: null,
  approve: null,
  removeFromMaintain: null,
  basic: null,
  readAllStatus: null,
  readDashboard: null,
})

/**
 * @enum
 */
export const ContactAttrs = keyMirror({
  emergencyContact: null,
  marketingContact: null,
  fireMarshalContact: null,
  workPermit: null,
})

/**
 * @enum
 */
export const contractorsAttrs = keyMirror({
  vendorContacts: null,
})

export const TenancySubAccountPermKeys = keyMirror({
  TenantProject: null,
  HandoverTakeback: null,
  TenancyRecord: null,
  Operation: null,
  StandaloneWorkPermits: null,
  GreenOperation: null,
  SubAdmin: null,
})

/**
 * @enum
 */
export const TenancySubAccountPerms = {
  [TenancySubAccountPermKeys.TenantProject]: {
    'update:any': ['*'],
  },
  [TenancySubAccountPermKeys.HandoverTakeback]: {
    'update:any': ['*'],
  },
  [TenancySubAccountPermKeys.TenancyRecord]: {
    'update:any': ['*'],
  },
  [TenancySubAccountPermKeys.Operation]: {
    'update:any': ['*'],
  },
  [TenancySubAccountPermKeys.StandaloneWorkPermits]: {
    'update:any': ['*'],
  },
  [TenancySubAccountPermKeys.GreenOperation]: {
    'update:any': ['*'],
  },
  [TenancySubAccountPermKeys.SubAdmin]: {
    'update:any': ['*'],
  },
}

/**
 * @enum
 */
export const ActionTypes = Object.freeze({
  createAny: 'create:any',
  createOwn: 'create:own',
  readAny: 'read:any',
  readOwn: 'read:own',
  updateAny: 'update:any',
  updateOwn: 'update:own',
  deleteAny: 'delete:any',
  deleteOwn: 'delete:own',
})

/**
 * @template T
 * @template {keyof typeof ActionTypes} K
 * @param {K} actionKey - action key.e.g "updateAny"
 * @param {T} attr - AC attribute name "approve"
 * @returns { `${typeof ActionTypes[K]}:${T}`} 完整的 action type 字符串
 */
const getActionType = (actionKey, attr) => {
  return `${ActionTypes[actionKey]}:${attr}`
}

/**
 * @enum
 */
export const GreenOperationActionTypeNames = keyMirror({
  updateAnyPreviewGreenOperation: null,
  updateAnyManageGreenOperation: null,
  updateAnyStartGreenOperation: null,
  updateAnySendGreenOperationReminder: null,
  updateAnySubmitGreenOperation: null,
  updateAnyReviewGreenOperation: null,
  updateAnyCommentGreenOperation: null,
  updateAnySendGreenOperation: null,
  updateAnyReadOperationPoints: null,
})

/**
 * @enum
 * @deprecated since 1.10.0 Because we have a new variable `newFullActionType`.
 */
export const FullActionTypes = {
  createAny: 'create:any:*',
  createOwn: 'create:own:*',
  readAny: 'read:any:*',
  readOwn: 'read:own:*',
  updateAny: 'update:any:*',
  updateOwn: 'update:own:*',
  deleteAny: 'delete:any:*',
  deleteOwn: 'delete:own:*',
  readAnyInfo: 'read:any:info',
  updateAnyDetail: 'update:any:detail',
  updateAnySend: 'update:any:send',
  updateAnyPreview: 'update:any:preview',
  updateAnyPreviewGreen: 'update:any:previewGreenFitout',
  updateAnyStartGreenFitout: 'update:any:startGreenFitout',
  updateAnyOnHoldGreenFitout: 'update:any:onHoldGreenFitout',
  updateAnyReviewGreenFitout: 'update:any:reviewGreenFitout',
  updateAnyCommentOnGreenFitout: 'update:any:commentOnGreenFitout',
  updateAnySendGreen: 'update:any:sendGreenFitout',
  [GreenOperationActionTypeNames.updateAnyPreviewGreenOperation]: 'update:any:previewOperation',
  [GreenOperationActionTypeNames.updateAnyStartGreenOperation]: 'update:any:startOperation',
  [GreenOperationActionTypeNames.updateAnySendGreenOperationReminder]: 'update:any:sendOperationReminder',
  [GreenOperationActionTypeNames.updateAnySubmitGreenOperation]: 'update:any:submitOperationDoc',
  [GreenOperationActionTypeNames.updateAnyReviewGreenOperation]: 'update:any:reviewOperation',
  [GreenOperationActionTypeNames.updateAnyCommentGreenOperation]: 'update:any:commentOperation',
  [GreenOperationActionTypeNames.updateAnySendGreenOperation]: 'update:any:sendOperation',
  [GreenOperationActionTypeNames.updateAnyReadOperationPoints]: 'update:any:readOperationPoints',
  readAnyDeposit: 'read:any:deposit',
  updateAnyDeposit: 'update:any:deposit',
  updateAnyAccounting: 'update:any:accounting',
  updateAnyResumeDeposit: 'update:any:resumeDeposit',
  readAnyInspection: 'read:any:inspection',
  updateAnyInspection: 'update:any:inspection',
  readAnyInsurance: 'read:any:insurance',
  updateAnyInsurance: 'update:any:insurance',
  updateAnyManageECircularContact: 'update:any:manageECircularContact',
  readAnyBasic: 'read:any:basic',
  readAllStatus: 'read:any:readAllStatus',
  readDashboard: 'read:any:readDashboard',
}

/**
 * @enum RCS
 */
export const Rcs = {
  Tender: 'ITQ/Tender',
  ContractorTender: 'ContractorITQ/Tender',
  ItqTenderDocument: 'ITQ/TenderDocument',
  ...keyMirror({
    'VendorProjectRepresentative': null,
    'HKLTenderRepresentative': null,
    'HKLProjectRepresentative': null,
    'HKLTenancyRepresentative': null,
    'VendorTenderRepresentative': null,
    'Threshold': null,
    'DepartmentVendor': null,
    'Project': null,
    'Settings': null,
    'ContractorProject': null,
    'StandaloneWorkPermits': null,
    'ContractorSettings': null,
    'ContractorContacts': null,
    'Tenant': null,
    'TenancyRecord': null,
    'Organization': null,
    'Contractor': null,
    'Contact': null,
    'ExportContact': null,
    'Directory': null,
    'Performance': null,
    'TenantTenancyRecord': null,
    'EngineerTenantProject': null,
    'WorkPermit': null,
    'Worker': null,
    'TenantProject': null,
    'TenantECircular': null,
    'VendorECircular': null,
    'CMSProjectContractor': null,
    'TMSProjectContractor': null,
    'TMSStandaloneContractor': null,
    'MoveInOut': null,
  }),
  Hotb: 'Handover/Takeback',
  TenantHotb: 'TenantHandoverTakeback',
}

export const ActionNameI18nMap = {
  'ITQ/Tender': {
    'read:any:*': 'settings.security.readOthers',
    'create:own:*': 'settings.security.createUpdate',
    'update:any:approve': 'settings.security.approve',
    'update:any:changePIC': 'settings.security.changePIC',
    'update:any:resetToDraft': 'settings.security.resetToDraft',
    'update:any:extendClosingDate': 'settings.security.extendClosingDate',
    'delete:any:*': 'settings.security.admin',
    [getActionType(Actions.readOwn, tenderReadOwnAttrs.exportViewAll)]: 'settings.security.exportViewAll',
  },
  Project: {
    'read:any:*': 'settings.security.read',
    'update:own:*': 'settings.security.manage',
    'update:any:changePIC': 'settings.security.changePIC',
    [getActionType(Actions.readOwn, projectReadOwnAttrs.exportViewAll)]: 'settings.security.exportViewAll',
  },
  Settings: {
    'read:any:basic': 'settings.security.read',
    'read:any:vendors': 'settings.security.settingsReadVendors',
    'update:any:basic': 'settings.security.update',
    'read:any:regional': 'settings.security.settingsReadRegional',
    'update:any:regional': 'settings.security.settingsUpdateRegional',
  },
  TenancyRecord: {
    'read:any:*': 'settings.security.read',
    'create:any:*': 'settings.security.create',
    'update:any:detail': 'settings.security.update',
    'update:any:manageMarketingContacts': 'settings.security.manageMarketingContacts',
    'update:any:requestForInvitation': 'settings.security.requestForInvitation',
    'update:any:inviteMaster': 'settings.security.inviteMaster',
    'update:any:leases': 'settings.security.linkLeases',
    'update:any:contacts': 'settings.security.linkContacts',
    'update:any:changeCompanyName': 'settings.security.changeCompanyName',
    'update:any:updateTenancyMasterAccount': 'settings.security.updateTenancyMasterAccount',
    'update:any:updateRemarksAndDocs': 'settings.security.updateRemarksAndDocs',
    'update:any:updateTenancyRep': 'settings.security.updateTenancyRep',
    'update:any:changeTenancyStatus': 'settings.security.changeTenancyStatus',
    'update:any:startOperation': 'settings.security.startOperation',
    'update:any:sendOperationReminder': 'settings.security.sendOperationReminder',
    'update:any:submitOperationDoc': 'settings.security.submitOperationDoc',
    'update:any:reviewOperation': 'settings.security.reviewOperation',
    'update:any:commentOperation': 'settings.security.commentOperation',
    'update:any:previewOperation': 'settings.security.previewOperation',
    'update:any:sendOperation': 'settings.security.sendOperation',
    'update:any:readOperationPoints': 'settings.security.readOperationPoints',
  },
  MoveInOut: {
    'read:any:*': 'settings.security.read',
    'update:any:*': 'settings.security.createUpdate',
  },
  Tenant: {
    'read:any:*': 'settings.security.read',
    'update:any:*': 'settings.security.update',
  },
  Organization: {
    'read:any:*': 'settings.security.read',
    'update:any:*': 'settings.security.createUpdate',
  },
  Contractor: {
    'read:any:*': 'settings.security.read',
    'update:any:*': 'settings.security.update',
  },
  Contact: {
    'read:any:*': 'settings.security.read',
    'update:any:*': 'settings.security.createUpdate',
  },
  ExportContact: {
    'read:any:emergencyContact': 'settings.security.exportEmergency',
    'read:any:marketingContact': 'settings.security.exportMarketing',
    'read:any:fireMarshalContact': 'settings.security.exportFireMarshal',
    'read:any:workPermit': 'settings.security.exportWorkPermit',
  },
  Performance: {
    'read:any:*': 'settings.security.readOnly',
  },
  'Handover/Takeback': {
    'read:any:*': 'settings.security.read',
    'create:any:*': 'settings.security.createCancel',
    'update:any:status': 'settings.security.approve',
    'update:any:arrange': 'settings.security.arrange',
    'update:any:others': 'settings.security.updateComplete',
    'update:any:resetToDraft': 'settings.security.resetToDraft',
    'update:any:changePIC': 'settings.security.changePIC',
  },
  EngineerTenantProject: {
    'read:any:info': 'settings.security.read',
    'read:any:deposit': 'settings.security.readDeposit',
    'read:any:inspection': 'settings.security.readInspection',
    'read:any:insurance': 'settings.security.readInsurance',
    'create:any:*': 'settings.security.createUpdate',
    'update:any:commentDesign': 'settings.security.commentDesign',
    'update:any:commentTech': 'settings.security.commentTech',
    'update:any:preview': 'settings.security.preview',
    'update:any:send': 'settings.security.send',
    'update:any:startGreenFitout': 'settings.security.startGreenFitout',
    'update:any:onHoldGreenFitout': 'settings.security.onHoldGreenFitout',
    'update:any:reviewGreenFitout': 'settings.security.reviewGreenFitout',
    'update:any:commentOnGreenFitout': 'settings.security.commentOnGreenFitout',
    'update:any:previewGreenFitout': 'settings.security.previewGreenFitout',
    'update:any:sendGreenFitout': 'settings.security.sendGreenFitout',
    'update:any:readGreenPoint': 'settings.security.readGreenPoint',
    'update:any:deposit': 'settings.security.updateDeposit',
    'update:any:accounting': 'tenantProjects.notifyAccounting',
    'update:any:inspection': 'settings.security.updateInspection',
    'update:any:insurance': 'settings.security.updateInsurance',
    'update:any:resumeDeposit': 'settings.security.resumeDeposit',
    'update:any:sendFinalDocsReminder': 'settings.security.sendFinalDocsReminder',
    'update:any:addReviewFinalDocFolder': 'settings.security.addReviewFinalDocFolder',
    'update:any:completeFinalDocs': 'settings.security.completeFinalDocs',
    'update:any:exemptFinalDocs': 'settings.security.exemptFinalDocs',
  },
  WorkPermit: {
    'read:any:*': 'settings.security.read',
    'update:any:reviewPP': 'settings.security.workPermit.reviewPP',
    'update:any:secondReviewPP': 'settings.security.workPermit.secondReviewPP',
    'update:any:approvePP': 'settings.security.workPermit.approvePP',
    'update:any:reviewWP': 'settings.security.workPermit.reviewWP',
    'update:any:secondReviewWP': 'settings.security.workPermit.secondReviewWP',
    'update:any:thirdReviewWP': 'settings.security.workPermit.thirdReviewWP',
    'update:any:approveWP': 'settings.security.workPermit.approveWP',
    'update:any:approveOfficeHP': 'settings.security.workPermit.approveOfficeHP',
    'update:any:reviewNonOfficeHP': 'settings.security.workPermit.reviewNonOfficeHP',
    'update:any:approveNonOfficeHP': 'settings.security.workPermit.approveNonOfficeHP',
    'update:any:followUpHoisting': 'settings.security.workPermit.followUpHoisting',
    'update:any:updateWorkers': 'settings.security.worker.update',
    'update:any:generateNotice': 'settings.security.workPermit.generateNotice',
    'update:any:reassignReviewer': 'settings.security.workPermit.reassignReviewer',
  },
  TenantECircular: {
    'read:any:*': 'settings.security.read',
    'create:any:*': 'settings.security.create',
    'update:any:*': 'settings.security.selectSend',
    'delete:any:*': 'action.cancel',
  },
  VendorECircular: {
    'read:any:*': 'settings.security.read',
    'create:any:*': 'settings.security.createUpdateSelectSendCancel',
    'update:any:manageECircularContact': 'settings.security.manageECircularContact',
  },
  Directory: {
    'read:any:basic': 'settings.security.directory.read',
    'read:any:readAllStatus': 'settings.security.directory.readAllStatus',
    'read:any:readDashboard': 'settings.security.directory.readDashboard',
    'update:any:createUpdate': 'settings.security.directory.update.createUpdate',
    'update:any:approve': 'settings.security.directory.update.approve',
    'update:any:maintainOrder': 'settings.security.directory.update.maintainOrder',
    'update:any:removeFromMaintain': 'settings.security.directory.update.removeFromMaintain',
  },
}

/**
 * @type {import('../../types/utils').getActionFullNameMap<typeof ActionNameI18nMap>}
*/
export function getActionFullNameMap (valMap) {
  return Object.values(valMap).reduce((rs, resource) => {
    lodash.forEach(resource, (_, key) => {
      const newKey = key.split(':').map(lodash.upperFirst).filter(v => v !== '*').join('')
      rs[newKey] = key
    })
    return rs
  }, {})
}

export const newFullActionType = getActionFullNameMap(ActionNameI18nMap)

/**
 * @enum
 */
export const ResourceNameMap = {
  'ITQ/Tender': 'settings.resources.ITQ',
  Contractor: 'settings.resources.contractor',
  Contact: 'settings.resources.contact',
  Directory: 'settings.resources.directory',
  Organization: 'settings.resources.organization',
  Tenant: 'settings.resources.tenant',
  TenancyRecord: 'settings.resources.tenancyRecord',
  EngineerTenantProject: 'settings.resources.tenantProject',
  TenantECircular: 'eCircular.tenantName',
  VendorECircular: 'eCircular.vendorName',
  MoveInOut: 'settings.resources.moveInOut',
  Performance: 'performance.title',
  Project: 'project.name',
  Settings: 'settings.title',
  ExportContact: 'settings.resources.exportcontact',
  'Handover/Takeback': 'settings.resources.handover_takeback',
  WorkPermit: 'settings.resources.workpermit',
}

export const aclPermsType = {
  tenders: {
    read: {rc: Rcs.Tender, action: Actions.readAny},
    createUpdate: {rc: Rcs.Tender, action: Actions.createOwn},
    approve: {rc: Rcs.Tender, action: Actions.updateAny, attr: NonTenantProjectAttr.approve},
    resetToDraft: {rc: Rcs.Tender, action: Actions.updateAny, attr: NonTenantProjectAttr.resetToDraft},
    changePIC: {rc: Rcs.Tender, action: Actions.updateAny, attr: NonTenantProjectAttr.changePIC},
    extendClosingDate: {rc: Rcs.Tender, action: Actions.updateAny, attr: NonTenantProjectAttr.extendClosingDate},
    review: {rc: Rcs.HKLTenderRepresentative, action: Actions.updateOwn},
    exportViewAll: { rc: Rcs.Tender, action: Actions.readOwn, attr: tenderReadOwnAttrs.exportViewAll },
  },
  projects: {
    read: {rc: Rcs.Project, action: Actions.readAny},
    manage: {rc: Rcs.Project, action: Actions.updateOwn, attr: allStarAttr},
    changePIC: {rc: Rcs.Project, action: Actions.updateAny, attr: NonTenantProjectAttr.changePIC},
    review: {rc: Rcs.HKLProjectRepresentative, action: Actions.updateOwn},
    exportViewAll: { rc: Rcs.Project, action: Actions.readOwn, attr: projectReadOwnAttrs.exportViewAll },
  },
  settings: {
    read: { rc: Rcs.Settings, action: Actions.readAny, attr: SettingsReadAnyAttrs.basic },
    readVendors: { rc: Rcs.Settings, action: Actions.readAny, attr: SettingsReadAnyAttrs.vendors },
    update: { rc: Rcs.Settings, action: Actions.updateAny, attr: SettingsUpdateAnyAttrs.basic },
    readRegional: { rc: Rcs.Settings, action: Actions.readAny, attr: SettingsReadAnyAttrs.regional },
    updateRegional: { rc: Rcs.Settings, action: Actions.updateAny, attr: SettingsUpdateAnyAttrs.regional },
  },
  performance: {
    read: {rc: Rcs.Performance, action: Actions.readAny},
  },
  itqTenderDocuments: {
    readUpdate: {rc: Rcs.ItqTenderDocument, action: Actions.updateOwn},
  },
  thresholds: {
    readUpdate: {rc: Rcs.Threshold, action: Actions.updateOwn},
  },
  departmentVendors: {
    read: {rc: Rcs.DepartmentVendor, action: Actions.readOwn},
  },
  vendorECircular: {
    read: { rc: Rcs.VendorECircular, action: Actions.readAny },
    manageECircularContact: {rc: Rcs.VendorECircular, action: Actions.updateAny, attr: VendorECircularAttrs.manageECircularContact},
    createUpdateSelectSendCancel: { rc: Rcs.VendorECircular, action: Actions.createAny },
  },
}

export const contractorPermsTypes = {
  tenders: {
    readUpdate: {rc: Rcs.ContractorTender, action: Actions.updateOwn},
    review: {rc: Rcs.VendorTenderRepresentative, action: Actions.updateOwn},
  },
  projects: {
    readUpdate: {rc: Rcs.ContractorProject, action: Actions.updateOwn},
    review: {rc: Rcs.VendorProjectRepresentative, action: Actions.updateOwn},
  },
  projectContractor: {
    readUpdate: { rc: Rcs.CMSProjectContractor, action: Actions.updateOwn },
  },
  standaloneWorkPermits: {
    readUpdate: {rc: Rcs.StandaloneWorkPermits, action: Actions.updateOwn},
  },
  settings: {
    read: { rc: Rcs.ContractorSettings, action: Actions.readOwn },
    update: { rc: Rcs.ContractorSettings, action: Actions.updateOwn },
    updateVendorContacts: { rc: Rcs.ContractorSettings, action: Actions.updateAny, attr: contractorsAttrs.vendorContacts },
  },
}

export const tenantPermsTypes = {
  tenancies: {
    readUpdate: { rc: Rcs.TenantTenancyRecord, action: Actions.updateOwn },
  },
  hotb: {
    readUpdate: { rc: Rcs.TenantHotb, action: Actions.updateOwn },
  },
  project: {
    readUpdate: { rc: Rcs.TenantProject, action: Actions.updateOwn },
  },
  projectContractor: {
    readUpdate: { rc: Rcs.TMSProjectContractor, action: Actions.updateOwn },
  },
  tenancyContractor: {
    readUpdate: { rc: Rcs.TMSStandaloneContractor, action: Actions.updateOwn },
  },
}

export const ccdPermsTypes = {
  tenant: {
    read: {rc: Rcs.Tenant, action: Actions.readAny},
    createUpdate: {rc: Rcs.Tenant, action: Actions.updateAny},
  },
  tenancy: {
    read: { rc: Rcs.TenancyRecord, action: Actions.readAny },
    create: { rc: Rcs.TenancyRecord, action: Actions.createAny },
    update: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.detail },
    requestForInvitation: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.requestForInvitation },
    manageMarketingContacts: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.manageMarketingContacts },
    [TenancyAttrs.startOperation]: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.startOperation },
    [TenancyAttrs.sendOperationReminder]: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.sendOperationReminder },
    [TenancyAttrs.submitOperationDoc]: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.submitOperationDoc },
    [TenancyAttrs.reviewOperation]: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.reviewOperation },
    [TenancyAttrs.commentOperation]: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.commentOperation },
    [TenancyAttrs.previewOperation]: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.previewOperation },
    [TenancyAttrs.sendOperation]: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.sendOperation },
    [TenancyAttrs.readOperationPoints]: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.readOperationPoints },
    inviteMaster: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.inviteMaster },
    linkContacts: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.contacts },
    linkLeases: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.leases },
    changeCompanyName: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.changeCompanyName },
    updateTenancyMasterAccount: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.updateTenancyMasterAccount },
    updateRemarksAndDocs: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.updateRemarksAndDocs },
    review: { rc: Rcs.HKLTenancyRepresentative, action: Actions.updateOwn },
    changeTenancyStatus: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.changeTenancyStatus },
    updateTenancyRep: { rc: Rcs.TenancyRecord, action: Actions.updateAny, attr: TenancyAttrs.updateTenancyRep },
  },
  moveInOut: {
    read: { rc: Rcs.MoveInOut, action: Actions.readAny },
    createUpdate: { rc: Rcs.MoveInOut, action: Actions.updateAny },
  },
  contractor: {
    read: { rc: Rcs.Contractor, action: Actions.readAny },
    createUpdate: { rc: Rcs.Contractor, action: Actions.updateAny },
  },
  organization: {
    read: { rc: Rcs.Organization, action: Actions.readAny },
    createUpdate: { rc: Rcs.Organization, action: Actions.updateAny },
  },
  contact: {
    read: { rc: Rcs.Contact, action: Actions.readAny },
    createUpdate: { rc: Rcs.Contact, action: Actions.updateAny },
  },
  exportContact: {
    exportEmergency: { rc: Rcs.ExportContact, action: Actions.readAny, attr: ContactAttrs.emergencyContact },
    exportMarketing: { rc: Rcs.ExportContact, action: Actions.readAny, attr: ContactAttrs.marketingContact },
    exportFireMarshal: { rc: Rcs.ExportContact, action: Actions.readAny, attr: ContactAttrs.fireMarshalContact },
    exportWorkPermit: { rc: Rcs.ExportContact, action: Actions.readAny, attr: ContactAttrs.workPermit },
  },
}

export const tmsPermsTypes = {
  directory: {
    read: { rc: Rcs.Directory, action: Actions.readAny, attr: DirectoryAttrs.basic },
    readAllStatus: { rc: Rcs.Directory, action: Actions.readAny, attr: DirectoryAttrs.readAllStatus },
    readDashboard: { rc: Rcs.Directory, action: Actions.readAny, attr: DirectoryAttrs.readDashboard },
    createUpdate: { rc: Rcs.Directory, action: Actions.updateAny, attr: DirectoryAttrs.createUpdate },
    approve: { rc: Rcs.Directory, action: Actions.updateAny, attr: DirectoryAttrs.approve },
    maintain: { rc: Rcs.Directory, action: Actions.updateAny, attr: DirectoryAttrs.maintainOrder },
    removeFromMaintain: { rc: Rcs.Directory, action: Actions.updateAny, attr: DirectoryAttrs.removeFromMaintain },
  },
  hotb: {
    read: { rc: Rcs.Hotb, action: Actions.readAny },
    create: { rc: Rcs.Hotb, action: Actions.createAny },
    approve: { rc: Rcs.Hotb, action: Actions.updateAny, attr: HotbAttrs.status },
    arrange: { rc: Rcs.Hotb, action: Actions.updateAny, attr: HotbAttrs.arrange },
    update: { rc: Rcs.Hotb, action: Actions.updateAny, attr: HotbAttrs.others },
    changePIC: { rc: Rcs.Hotb, action: Actions.updateAny, attr: HotbAttrs.changePIC },
    resetToDraft: { rc: Rcs.Hotb, action: Actions.updateAny, attr: HotbAttrs.resetToDraft },
  },
  project: {
    read: { rc: Rcs.EngineerTenantProject, action: Actions.readAny, attr: TenantProjectAttrs.info },
    createUpdate: { rc: Rcs.EngineerTenantProject, action: Actions.createAny },
    commentDesign: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.commentDesign },
    commentTech: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.commentTech },
    preview: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.preview },
    send: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.send },
    startGreenFitout: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.startGreenFitout },
    onHoldGreenFitout: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.onHoldGreenFitout },
    reviewGreenFitout: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.reviewGreenFitout },
    commentOnGreenFitout: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.commentOnGreenFitout },
    readGreenPoint: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.readGreenPoint },
    previewGreenFitout: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.previewGreenFitout },
    sendGreenFitout: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.sendGreenFitout },
    readDeposit: { rc: Rcs.EngineerTenantProject, action: Actions.readAny, attr: TenantProjectAttrs.deposit },
    updateDeposit: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.deposit },
    resumeDeposit: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.resumeDeposit },
    notifyAccounting: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.accounting },
    readInspection: { rc: Rcs.EngineerTenantProject, action: Actions.readAny, attr: TenantProjectAttrs.inspection },
    updateInspection: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.inspection },
    readInsurance: { rc: Rcs.EngineerTenantProject, action: Actions.readAny, attr: TenantProjectAttrs.insurance },
    updateInsurance: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.insurance },
    sendFinalDocsReminder: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.sendFinalDocsReminder },
    addReviewFinalDocFolder: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.addReviewFinalDocFolder },
    completeFinalDocs: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.completeFinalDocs },
    exemptFinalDocs: { rc: Rcs.EngineerTenantProject, action: Actions.updateAny, attr: TenantProjectAttrs.exemptFinalDocs },
  },
  workpermit: {
    read: { rc: Rcs.WorkPermit, action: Actions.readAny },
    reviewPP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.reviewPP },
    secondReviewPP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.secondReviewPP },
    approvePP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.approvePP },
    reviewWP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.reviewWP },
    secondReviewWP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.secondReviewWP },
    thirdReviewWP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.thirdReviewWP },
    approveWP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.approveWP },
    approveOfficeHP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.approveOfficeHP },
    reassignReviewer: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.reassignReviewer },
    reviewNonOfficeHP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.reviewNonOfficeHP },
    approveNonOfficeHP: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.approveNonOfficeHP },
    followUpHoisting: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.followUpHoisting },
    updateWorkers: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.updateWorkers },
    generateNotice: { rc: Rcs.WorkPermit, action: Actions.updateAny, attr: WorkPermitAttrs.generateNotice },
  },
  tenantEcircular: {
    read: { rc: Rcs.TenantECircular, action: Actions.readAny },
    createUpdate: { rc: Rcs.TenantECircular, action: Actions.createAny },
    send: { rc: Rcs.TenantECircular, action: Actions.updateAny },
    cancel: { rc: Rcs.TenantECircular, action: Actions.deleteAny },
  },
}

/**
 * The peering variable is `PermTypeMaps` in the back-end code base
 * That is for getting the permissions by the key name.
 * @enum
 */
export const PermTypeMapKeys = keyMirror({
  APPROVE_DIRECTORY: null,
  UPDATE_DIRECTORY: null,
  MAINTAIN_DIRECTORY: null,

  DIRECTORY_SUBMITTED_BCC_GROUP: null,
  DIRECTORY_APPROVED_BCC_GROUP: null,
  DIRECTORY_REJECTED_BCC_GROUP: null,

  PROJECT_MANAGE: null,
  PROJECT_PERMIT_BCC_GROUP: null,
  PROJECT_PERMIT_APPROVED_BCC_GROUP: null,
  WORK_PERMIT_BCC_GROUP: null,
  APPROVE_OFFICE_HOURS_HOISTING: null,
  REVIEW_AFTER_OFFICE_HOURS_HOISTING: null,
  APPROVE_AFTER_OFFICE_HOURS_HOISTING: null,
  APPROVE_PROJECT_PERMIT: null,
  CCD_TENANT_CONTACT_CREATE_UPDATE: null,
  ECIRCULAR_SEND: null,
  GREEN_OPERATION_NEXT_REVIEWER: null,
  GREEN_OPERATION_SEND: null,
  HOTB_ARRANGE: null, // For HOTB PM-SEC
  HOTB_ARRANGE_UPDATE_COMPLETE: null,
  HOTB_ARRANGE_APPROVE_UPDATE_COMPLETE: null,
  HOTB_CREATE_APPROVE: null,
  HOTB_CREATE_CANCEL: null,
  HOTB_APPROVE: null,
  HOTB_UPCOM_CCD_TENANT_CONTACT_CRUP: null,
  HOTB_UPDATE_COMPLETE: null, // For HOTB PM/TM
  HOTB_APPROVE_UPDATE_COMPLETE: null,
  INVITE_MASTER_ACCOUNT: null,
  INVITE_TENANCY_MASTER: null,
  TENDER_CREATE_UPDATE: null,
  TENANT_PROJECT_DESIGN: null,
  TENANT_PROJECT_NOTIFY_ACCOUNTING: null,
  TENANT_PROJECT_RELATED_UPDATE: null,
  TENANT_PROJECT_FINAL_DOCS_COMP_EXEP: null,
  TENANT_PROJECT_RELATED_UPDATE_NO_DESIGN: null,
  TENANT_PROJECT_SEND: null,
  TENANT_PROJECT_TECH_PREV_SEND: null,
  TENANT_PROJECT_TECH_SEND: null,
  TENANT_PROJECT_UPDATE: null,
  TENANT_PROJECT_UPDATE_DEPOSIT: null,
  TENANT_PROJECT_REQUEST_REFUND: null,
  TENANT_PROJECT_UPDATE_INSPECTION: null,
  HOTB_MOVEINOUT_NOTIFICATION: null,
  CCD_MOVEINOUT_NOTIFICATION: null,
  TENANT_PROJECT_COMMENT_TECH: null,
  TMWORKPERMIT_REVIEW_1ST: null,
  TMWORKPERMIT_REVIEW_2ND: null,
  TMWORKPERMIT_APPR: null,
  WORKPERMIT_REVIEW: null,
  WORKPERMIT_APPR: null,
  HP_NOOFFICEHOUR_REVIEW: null,
  HP_NOOFFICEHOUR_APPROVE: null,
  GREEN_FIT_OUT_COMPLETE_CC: null,
  GREEN_FIT_OUT_NEXT_REVIEWER: null,
})

export const aclHelper = {
  /**
   * @param {{ rc: string, action: string, attr: string? }} permType
   * @returns {boolean}
   */
  checkCustomPermission (perms, permType) {
    const role = 'tmpUser'
    const acl = new AccessControl({
      [role]: perms,
    })
    const permission = acl.can(role)[permType.action](permType.rc)
    return permType.attr
      ? permission.granted && permission.attributes.includes(permType.attr)
      : permission.granted
  },
  /**
   * @param {{ rc: string, action: string, attr: string? }} permType
   * @returns {boolean}
   */
  getAccess (permType) {
    const rolesAccess = store.state.access.perms
    if (rolesAccess) {
      const ACL = new AccessControl(rolesAccess)
      return Object.keys(rolesAccess).some(roleId => {
        const canObj = ACL.can(roleId)
        let permission = canObj[permType.action](permType.rc)
        const granted = permType.attr
          ? permission.granted && permission.attributes.includes(permType.attr)
          : permission.granted
        return granted
      })
    }
    return false
  },
  hasWorkPermitAccess (isContractor, isTenant) {
    return (isContractor && aclHelper.getAccess(contractorPermsTypes.projectContractor.readUpdate)) ||
      (isTenant && aclHelper.getAccess(tenantPermsTypes.projectContractor.readUpdate))
  },
  hasStandaloneWpAccess (isContractor) {
    return isContractor && aclHelper.getAccess(contractorPermsTypes.standaloneWorkPermits.readUpdate)
  },
  hasProjectsAccess (isContractor = false) {
    const perms = []
    if (isContractor) {
      perms.push(
        contractorPermsTypes.projects.readUpdate,
        contractorPermsTypes.projects.review,
      )
    } else {
      perms.push(
        aclPermsType.projects.read,
        aclPermsType.projects.manage,
        aclPermsType.projects.review,
      )
    }
    return perms.some(perm => aclHelper.getAccess(perm))
  },
  hasTendersAccess (isContractor = false) {
    if (isContractor) {
      return aclHelper.getAccess(contractorPermsTypes.tenders.readUpdate)
    } else {
      return aclHelper.getAccess(aclPermsType.tenders.read) ||
        aclHelper.getAccess(aclPermsType.tenders.createUpdate) ||
        aclHelper.getAccess(aclPermsType.tenders.approve) || aclHelper.getAccess(aclPermsType.tenders.review)
    }
  },
  hasTenderRepAccess (isContractor = false) {
    return isContractor
      ? aclHelper.getAccess(contractorPermsTypes.tenders.review)
      : aclHelper.getAccess(aclPermsType.tenders.review)
  },
  hasPerformanceAccess (isContractor = false) {
    if (!isContractor) {
      return aclHelper.getAccess(aclPermsType.performance.read)
    } else {
      return false
    }
  },
  hasSettingsAccess () {
    return [
      aclPermsType.settings.read,
      aclPermsType.itqTenderDocuments.readUpdate,
      aclPermsType.thresholds.readUpdate,
      aclPermsType.departmentVendors.read,
      aclPermsType.vendorECircular.manageECircularContact,
      aclPermsType.settings.readVendors,
    ].some(val => aclHelper.getAccess(val))
  },
  hasTenantProjectsAccess () {
    return aclHelper.getAccess(tmsPermsTypes.project.read) || aclHelper.getAccess(tmsPermsTypes.hotb.read)
  },
  hasContactsAccess (isContractor = false) {
    if (isContractor) {
      return false
    } else {
      return aclHelper.getAccess(ccdPermsTypes.contact.read) ||
        aclHelper.getAccess(ccdPermsTypes.tenant.read) ||
        aclHelper.getAccess(ccdPermsTypes.contractor.read) ||
        aclHelper.getAccess(ccdPermsTypes.organization.read)
    }
  },
  hasEcircularRead () {
    return aclHelper.getAccess(aclPermsType.vendorECircular.read) || aclHelper.getAccess(tmsPermsTypes.tenantEcircular.read)
  },
  hasTenantEcircularRead () {
    return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.read)
  },
  hasUpdateHotbAccess () {
    return aclHelper.getAccess(tmsPermsTypes.hotb.create) ||
      aclHelper.getAccess(tmsPermsTypes.hotb.approve) ||
      aclHelper.getAccess(tmsPermsTypes.hotb.update) ||
      aclHelper.getAccess(tmsPermsTypes.hotb.arrange)
  },
  hasMaintainAccess () {
    return aclHelper.getAccess(tmsPermsTypes.directory.maintain) || aclHelper.getAccess(tmsPermsTypes.directory.removeFromMaintain)
  },
  getSettingsUpdateAccess () {
    return aclHelper.getAccess(aclPermsType.settings.update)
  },
  hasUpdateVendorContactsAccess () {
    return aclHelper.getAccess(contractorPermsTypes.settings.updateVendorContacts)
  },
  hasSettingsReadAccess () {
    return aclHelper.getAccess(aclPermsType.settings.read)
  },
  hasTmsAccess (permType) {
    if (aclHelper.getAccess(permType)) return true
    let has = false
    const user = store.state.user.currentUser || {}
    const roles = user.roles || []
    roles.every(role => {
      let perms = role.perms || []
      if (perms && typeof perms === 'object') {
        perms = [perms]
      }
      return perms.every(perm => {
        const matched = Object.entries(perm).filter(([resource, action]) => resource === permType.rc && action === permType.action)
        if (matched.length) {
          has = true
          return false
        }
        return true
      })
    })
    return has
  },
  hasDirectoryAccess () {
    return aclHelper.getAccess(tmsPermsTypes.directory.read) || aclHelper.getAccess(tmsPermsTypes.directory.approve)
  },
  showTmsDashboard () {
    return [...Object.values(tmsPermsTypes.project),
      ...Object.values(tmsPermsTypes.hotb),
      ...Object.values(ccdPermsTypes.tenancy),
      tmsPermsTypes.directory.readDashboard]
      .some(access => aclHelper.getAccess(access))
  },
  showWorkPermitDashboard () {
    return [...Object.values(tmsPermsTypes.workpermit)].some(access => aclHelper.getAccess(access))
  },
  isPicView () {
    const id = (store.state.user.currentUser || {}).id
    const currentTender = store.state.tenders.currentTender
    return id === currentTender.general.managedBy
  },
  isProjectPic () {
    const id = (store.state.user.currentUser || {}).id
    const currentProject = store.state.projects.currentProject
    return id === currentProject.managedBy
  },
  isVendorAccount () {
    return store.getters['user/isVendorAccount']
  },
  showExportContacts () {
    const featureStatus = store.getters['regions/featureStatus'] || {}
    const canExportWorkPermits = !!featureStatus[FEATURE_NAMES.WORK_PERMITS] &&
      aclHelper.getAccess(ccdPermsTypes.exportContact.exportWorkPermit)
    return aclHelper.getAccess(ccdPermsTypes.exportContact.exportEmergency) ||
      aclHelper.getAccess(ccdPermsTypes.exportContact.exportMarketing) ||
      aclHelper.getAccess(ccdPermsTypes.exportContact.exportFireMarshal) ||
      aclHelper.getAccess(tmsPermsTypes.workpermit.read) ||
      canExportWorkPermits
  },
}
